<template>
  <transition name="fade">
    <div
      v-show="isActive || isActiveDelay"
      ref="velmldRef"
      :class="{ 'velmld-full-screen': isFullScreen }"
      :style="{ backgroundColor }"
      class="velmld-overlay"
    >
      <div class="velmld-spinner">
        <slot name="default">
          <component
            :is="spinnerComponent"
            :color="color"
            :size="`${size}px`"
            :duration="`${duration}s`"
          />
        </slot>
        <div v-if="text.length" :style="{ color, ...textStyle }">
          {{ text }}
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
// Forked from https://github.com/biigpongsatorn/vue-element-loading
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";

import LineScale from "@/components/ui/vue-element-loading/loaders/line-scale.vue";
import Spinner from "@/components/ui/vue-element-loading/loaders/spinner.vue";

import type { CSSProperties } from "vue";

type VueElementLoadingProps = {
  active?: boolean;
  spinner?: "spinner" | "line-scale";
  text?: string;
  textStyle?: CSSProperties;
  color?: string;
  isFullScreen?: boolean;
  backgroundColor?: string;
  size?: string;
  duration?: string;
  delay?: string | number;
};

const {
  active = false,
  spinner = "spinner",
  text = "",
  textStyle = {},
  color = "#000",
  isFullScreen = false,
  backgroundColor = "rgba(255, 255, 255, .9)",
  size = "40",
  duration = "0.6",
  delay = 0,
} = defineProps<VueElementLoadingProps>();

const SPINNER_COMPONENTS = {
  "line-scale": LineScale,
  spinner: Spinner,
} as const;

const velmldRef = useTemplateRef<HTMLDivElement>("velmldRef");

const isActive = ref(active);
const isActiveDelay = ref(false);

const spinnerComponent = computed(() => SPINNER_COMPONENTS[spinner]);

function delayActive(ms: number) {
  isActiveDelay.value = true;

  setTimeout(() => {
    isActiveDelay.value = false;
  }, ms);
}

watch(
  () => active,
  (value) => {
    isActive.value = value;

    if (value) {
      velmldRef.value?.parentElement?.classList.add("velmld-parent");
    }
  }
);

onMounted(() => {
  velmldRef.value?.parentElement?.classList.add("velmld-parent");

  if (delay) {
    const delayMs = +delay * 1000;
    delayActive(delayMs);
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.velmld-overlay {
  position: absolute;
  z-index: 3000;
  margin: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition: opacity 0.3s;
}
.velmld-spinner {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
  text-align: center;
}
.velmld-full-screen {
  position: fixed;
}
</style>

<style>
.velmld-parent {
  position: relative !important;
}
</style>
